// RMA (return merchandise authorization) API client for the marketplace
// customer embed — My Account C1, task T7b, design.md AC5/AC6.
//
// Wire shape mirrors the Go model exactly (project rule §39):
//   model: internal/marketplace/model/rma.go (RMARequest)
//   handler: internal/marketplace/handler/customer_handler_rma.go
//     - HandleRMACreate (POST /api/v1/marketplace/me/rma)
//     - HandleRMAList   (GET  /api/v1/marketplace/me/rma)
//
// UNLIKE address-api.ts's list envelope (`{ addresses: [...] }` — a Go
// handler that wraps the array in a nested key), HandleRMAList emits
// `{"data": reqs}` directly — the array IS the .data payload, no nested
// key. Verified by reading customer_handler_rma.go:90
// (`writeJSON(w, http.StatusOK, map[string]interface{}{"data": reqs})`).
// So after embedApiFetch's .data unwrap, listRMARequests() below returns
// RMARequestWire[] directly — do not wrap it in an object.
//
// No created_at/updated_at on the wire (project rule #9 — database-internal,
// never in Go models or API responses), so there is no request timestamp to
// display; the UI only has `status` to show.
//
// `id` and the FK ids are UUID strings (not numeric, unlike AddressWire's
// `id`) — mirrors OrderLine.id / Product.id already in embed-shared/types.ts.

import { embedApiFetch } from "../../embed-shared/api-client";

/** RMAStatus constants — mirrors model/rma.go's RMAStatus* consts exactly. */
export const RMA_STATUS_REQUESTED = "requested";
export const RMA_STATUS_APPROVED = "approved";
export const RMA_STATUS_REJECTED = "rejected";
export const RMA_STATUS_COMPLETED = "completed";

/**
 * "Open" per design.md AC5/AC6 + plan.md T7b: a request that still blocks a
 * new one for the same order line (the server's uq_rma_open_per_line partial
 * unique index covers exactly these two statuses — see repo.ErrRMAOpenRequestExists's
 * 409 in the handler). Rejected/completed are terminal — NOT open.
 */
export const OPEN_RMA_STATUSES: ReadonlySet<string> = new Set([
  RMA_STATUS_REQUESTED,
  RMA_STATUS_APPROVED,
]);

/**
 * RMA request as returned by the Go API. Mirrors model.RMARequest exactly.
 * Go json tags: id, customer_id, order_line_id, product_id, reason,
 * note (omitempty), status, quantity, version.
 */
export interface RMARequestWire {
  id: string; // UUID
  customer_id: number;
  order_line_id: string; // UUID
  product_id: string; // UUID
  reason: string;
  note?: string; // nullable, omitted when null (Go omitempty)
  status: string; // "requested" | "approved" | "rejected" | "completed"
  quantity: number; // units of the line this request returns (>= 1)
  version: number;
}

/**
 * Request body for POST /api/v1/marketplace/me/rma.
 * Mirrors the handler's `body` struct. customer_id is sourced from JWT
 * (never sent by client — security: prevents cross-customer create).
 */
export interface CreateRMARequest {
  order_line_id: string;
  reason: string;
  note?: string;
  /** Units to return (integer >= 1). Omit for "all remaining returnable
   *  units" — the server's meaning of an absent quantity. 422
   *  RMA_QUANTITY_EXCEEDED when it exceeds what remains. */
  quantity?: number;
}

const RMA_PATH = "/api/v1/marketplace/me/rma";

/**
 * List all RMA requests for the authenticated customer.
 *
 * GET /api/v1/marketplace/me/rma
 * Response (after embedApiFetch .data unwrap): RMARequestWire[] directly —
 * see file-header note on the (lack of a) nested envelope key.
 */
export async function listRMARequests(): Promise<RMARequestWire[]> {
  const result = await embedApiFetch<RMARequestWire[]>(RMA_PATH);
  return result ?? [];
}

/**
 * Create a new RMA request against one of the authenticated customer's own
 * order lines.
 *
 * POST /api/v1/marketplace/me/rma
 * Response (after embedApiFetch .data unwrap): RMARequestWire (version=1).
 * Throws ApiError(404) if order_line_id doesn't belong to this customer
 * (uniform — indistinguishable from "doesn't exist", project §39/design.md's
 * "never render ownership failures differently" convention), ApiError(422,
 * "VALIDATION_ERROR") if reason is empty, ApiError(409,
 * "RMA_OPEN_REQUEST_EXISTS") when an open request already exists for that
 * line — callers MUST surface this message verbatim and NOT retry.
 */
export async function createRMARequest(req: CreateRMARequest): Promise<RMARequestWire> {
  return embedApiFetch<RMARequestWire>(RMA_PATH, { method: "POST", body: req });
}
